import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import PagamentoRepository from 'modules/pagamentos/infra/repositories/PagamentoRepository'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import { IPagamentoBoletoResponse } from '../domain/entities/IPagamentoBoletoResponse'
import { IPagarBoletoResponseDTO } from '../domain/entities/IPagarBoletoResponseDTO'
import { encrypt } from '../../../shared/utils/crypt'

interface IPagamentoBoletoContextState {
  consultarDadosBoleto(codigoBarras: string): Promise<boolean>
  pagarBoleto(
    senha: string,
    pin: string,
    token: string,
  ): Promise<IPagarBoletoResponseDTO | undefined>
  setRequestError: React.Dispatch<React.SetStateAction<string>>
  boleto: IPagamentoBoletoResponse
  boletoDeposito: IPagamentoBoletoResponse
  setBoleto(boleto: IPagamentoBoletoResponse): void
  setBoletoDeposito(boleto: IPagamentoBoletoResponse): void
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  requestError: string
  step: number
  voltar: () => void
  avancar: () => void
  limpar: () => void
}

const PagamentoBoletoContext = createContext<IPagamentoBoletoContextState>(
  {} as IPagamentoBoletoContextState,
)

export function usePagamentoBoleto(): IPagamentoBoletoContextState {
  const context = useContext(PagamentoBoletoContext)

  if (!context) {
    throw new Error(
      'usePagamentoBoleto must be used within an PagamentoBoletoProvider',
    )
  }

  return context
}

export const PagamentoBoletoProvider: React.FC = ({ children }) => {
  const pagamentoRepository = useMemo(() => {
    return new PagamentoRepository()
  }, [])

  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [requestError, setRequestError] = useState('')

  const [boleto, setBoleto] = useState<IPagamentoBoletoResponse>(
    {} as IPagamentoBoletoResponse,
  )

  const [boletoDeposito, setBoletoDeposito] =
    useState<IPagamentoBoletoResponse>({} as IPagamentoBoletoResponse)
  const { frotistaId } = useFrotista()

  const consultarDadosBoleto = useCallback(
    async (codigoBarras: string) => {
      setLoading(true)
      setRequestError('')
      try {
        const response = await pagamentoRepository.consultarDadosBoleto(
          codigoBarras,
          frotistaId,
        )
        setBoleto(response)
        setLoading(false)
        return true
      } catch (e: unknown) {
        setLoading(false)
        setBoleto({} as IPagamentoBoletoResponse)
        if (e instanceof Error) {
          setRequestError(e.message)
        } else {
          setRequestError('Erro ao consultar dados do Boleto')
        }
        return false
      }
    },
    [frotistaId, boleto],
  )

  const pagarBoleto = useCallback(
    async (senha: string, pin: string, token: string) => {
      try {
        setLoading(true)
        setRequestError('')

        const payload = {
          body: encrypt(
            {
              codigo_barras: boleto.codigo_barras,
              valor: boleto.valor_total ?? boleto.valor,
              valor_original: boleto?.valor_original ?? boleto?.valor,
              is_web: true,
              senha,
              pin,
              token,
            },
            false,
            frotistaId.toString(),
          ),
          frotista_id: frotistaId,
        }

        return await pagamentoRepository.pagarBoleto(payload)
      } catch (e: unknown) {
        if (e instanceof Error) {
          setRequestError(e.message)
        } else {
          setRequestError('Erro ao buscar extrato da conta')
        }
      } finally {
        setLoading(false)
      }
    },
    [frotistaId, boleto],
  )

  const voltar = useCallback(() => {
    setCurrentStep(currentStep - 1)
  }, [currentStep])

  const avancar = useCallback(() => {
    setCurrentStep(currentStep + 1)
  }, [currentStep])

  const limpar = () => {
    setBoleto(undefined)
    setBoletoDeposito(undefined)
    setLoading(false)
    setRequestError(null)
    setCurrentStep(0)
  }

  const handleSetBoleto = useCallback(
    (boleto: IPagamentoBoletoResponse) => {
      setBoleto(boleto)
    },
    [boleto],
  )

  const handleSetBoletoDeposito = useCallback(
    (boletoDeposito: IPagamentoBoletoResponse) => {
      setBoletoDeposito(boletoDeposito)
    },
    [boletoDeposito],
  )

  return (
    <PagamentoBoletoContext.Provider
      value={{
        consultarDadosBoleto,
        pagarBoleto,
        setRequestError,
        boleto,
        avancar,
        voltar,
        loading,
        setLoading,
        requestError,
        step: currentStep,
        limpar,
        setBoleto: handleSetBoleto,
        boletoDeposito,
        setBoletoDeposito: handleSetBoletoDeposito,
      }}
    >
      {children}
    </PagamentoBoletoContext.Provider>
  )
}
